<template>
  <div>
    <div class="storage-package-card">
      <div class="card-content" style="flex-direction: row; justify-content: space-between">
        <div class="package-info">
          <div class="card-header">
            <a-select
              v-model="selectedTenant"
              style="width: 200px"
              size="small"
              @change="handleTenantChange"
              :loading="loading"
            >
              <a-select-option
                v-for="tenant in tenants"
                :key="tenant.id"
                :value="tenant.id"
              >
                {{ tenant.name }}
              </a-select-option>
            </a-select>
          </div>
          <div class="current-package">
            <span class="label">当前套餐:</span>
            <span
              class="value"
              :class="{
                'not-purchased': !hasPackage,
                'expired': hasPackage && isExpired
              }"
            >
              {{ packageStatusText }}
            </span>
          </div>
          <div class="expire-time">
            <span class="label">到期时间:</span>
            <span class="value" :class="{ 'expired': hasPackage && isExpired }">
              {{ expireTimeText }}
            </span>
          </div>
          <div class="capacity-info" v-if="hasPackage">
            <div class="capacity-item">
              <span class="label">总容量:</span>
              <span class="value">{{ $formatCapacity(currentStorage.totalCapacity) }}</span>
            </div>
            <div class="capacity-item">
              <span class="label">已使用:</span>
              <span class="value">{{ $formatCapacity(currentStorage.totalUsedCapacity) }}</span>
            </div>
            <div class="capacity-item">
              <span class="label">剩余容量:</span>
              <span class="value">{{ $formatCapacity(currentStorage.remainingCapacity) }}</span>
            </div>
            <div class="capacity-item" v-if="currentStorage.sharedFrozenCapacity > 0">
              <span class="label">共享冻结容量:</span>
              <span class="value">{{ $formatCapacity(currentStorage.sharedFrozenCapacity) }}</span>
            </div>
          </div>
        </div>
        <div class="storage-usage" v-if="hasPackage">
          <a-progress
            type="circle"
            :percent="usagePercent"
            :width="150"
            :stroke-width="8"
            :stroke-color="progressColor"
          />
          <div class="usage-text">已用 {{ usedCapacityText }} / 可用 {{ totalCapacityText }}</div>
        </div>
      </div>
    </div>

    <div
      class="storage-package-card"
      style="margin-top: 40px"
      v-if="!hasPackage || isExpired"
    >
      <div class="card-content">
        <div class="prompt-message">
          <div class="message-icon">
            <img
              src="http://hoy-test-1308702491.cos.ap-beijing.myqcloud.com/hoy/oth/IMG_20251105_134842__265773.png"
              alt=""
            />
          </div>
          <div class="message-title">{{ messageTitle }}</div>
          <div class="message-content">{{ messageContent }}</div>
        </div>
        <div class="action-button">
          <a-button
            type="primary"
            class="purchase-btn"
            @click="goToPay"
            :loading="loading"
          >
            {{ buttonText }}
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getStorageInfo } from '@/api/VR/storage'
import { message } from 'ant-design-vue'

export default {
  name: 'StoragePackageCard',
  data() {
    return {
      loading: false,
      selectedTenant: null,
      tenants: [],
      storageData: [], // 改为数组格式
    }
  },
  computed: {
    // 获取当前选中的存储套餐
    currentStorage() {
      if (!this.storageData.length) return {}

      // 如果有选中的租户，返回对应租户的存储信息
      if (this.selectedTenant) {
        return this.storageData.find(item => item.id === this.selectedTenant) || this.storageData[0]
      }

      // 默认返回第一个存储套餐
      return this.storageData[0]
    },
    hasPackage() {
      return this.storageData && this.storageData.length > 0
    },
    isExpired() {
      if (!this.hasPackage || !this.currentStorage.expireTime) return false
      return new Date(this.currentStorage.expireTime) < new Date()
    },
    packageStatusText() {
      if (!this.hasPackage) return '未购买'
      if (this.isExpired) return '已过期'
      return `套餐 ${this.currentStorage.packageId}`
    },
    expireTimeText() {
      if (!this.hasPackage) return '-'
      if (!this.currentStorage.expireTime) return '-'

      const date = new Date(this.currentStorage.expireTime)
      return date.toLocaleDateString()
    },
    usagePercent() {
      if (!this.hasPackage || !this.currentStorage.totalCapacity) return 0

      const percent = (this.currentStorage.totalUsedCapacity / this.currentStorage.totalCapacity) * 100
      return Math.min(Math.round(percent * 100) / 100, 100) // 限制最大100%
    },
    progressColor() {
      if (this.usagePercent > 90) return '#ff4d4f'
      if (this.usagePercent > 70) return '#faad14'
      return '#52c41a'
    },
    usedCapacityText() {
      if (!this.hasPackage) return '0GB'
      return this.$formatCapacity(this.currentStorage.totalUsedCapacity)
    },
    totalCapacityText() {
      if (!this.hasPackage) return '0GB'
      return this.$formatCapacity(this.currentStorage.totalCapacity)
    },
    messageTitle() {
      if (!this.hasPackage) return '您尚未购买存储套餐'
      if (this.isExpired) return '您的存储套餐已过期'
      return ''
    },
    messageContent() {
      if (!this.hasPackage) return '请购买合适的存储套餐以开始使用我们的存储空间服务'
      if (this.isExpired) return '请续费存储套餐以继续使用我们的存储空间服务'
      return ''
    },
    buttonText() {
      if (!this.hasPackage) return '立即购买套餐'
      if (this.isExpired) return '立即续费'
      return '立即购买套餐'
    },
  },
  mounted() {
    this.fetchStorageData()
  },
  methods: {
    async fetchStorageData() {
      try {
        this.loading = true
        // 使用导入的接口函数
        const response = await getStorageInfo()

        if (response && Array.isArray(response.data)) {
          this.storageData = response.data

          // 从返回的数据中提取租户信息
          if (this.storageData.length > 0) {
            this.tenants = this.storageData.map(item => ({
              id: item.id,
              name: item.tenantName,
            }))

            // 默认选择第一个租户
            this.selectedTenant = this.tenants[0].id
          }
        } else {
          // 如果接口返回格式不符合预期
          console.warn('接口返回数据格式不符合预期:', response)
          this.storageData = []
        }
      } catch (error) {
        console.error('获取存储套餐信息失败:', error)
        message.error('获取存储套餐信息失败')
        this.storageData = []
      } finally {
        this.loading = false
      }
    },

    handleTenantChange(tenantId) {
      this.selectedTenant = tenantId
      // 不需要重新获取数据，因为所有租户的数据已经一次性获取
    },

    goToPay() {
      this.$router.push({ name: 'BuyNow' })
    },
  },
}
</script>

<style scoped>
.storage-package-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 40px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}
.message-icon img {
  width: 60px;
  height: 60px;
  margin-bottom: 16px;
  border-radius: 50%;
}

.card-header {
  margin-bottom: 20px;
}

.company-name {
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.package-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.current-package,
.expire-time {
  display: flex;
  align-items: center;
}

.label {
  font-size: 14px;
  color: #666666;
  line-height: 1.5;
  min-width: 100px;
}

.value {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.not-purchased {
  color: #ff4d4f;
}

.expired {
  color: #faad14;
}

.capacity-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.capacity-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.storage-usage {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-right: 100px;
}

.usage-text {
  font-size: 14px;
  color: #262626;
  line-height: 1.5;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-inner {
  height: 100%;
  background: #1890ff;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.prompt-message {
  text-align: center;
  padding: 16px 0;
}

.message-title {
  font-size: 16px;
  font-weight: bold;
  color: #262626;
  line-height: 1.5;
  margin-bottom: 4px;
}

.message-content {
  font-size: 14px;
  color: #666666;
  line-height: 1.5;
}

.action-button {
  text-align: center;
}

.purchase-btn {
  height: 40px;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  background: #1890ff;
  border-color: #1890ff;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.045);
}

.purchase-btn:hover,
.purchase-btn:focus {
  background: #40a9ff;
  border-color: #40a9ff;
}
</style>
